import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, X, Star, Home, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty } from "@/components/common";
import { COUNTRIES, isUS, postalLabel, countryName } from "@/lib/countries";

const BLANK = { name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US", is_default: false };

function AddressModal({ address, onClose, onSaved }) {
  const editing = !!address;
  const [f, setF] = useState(address ? { ...BLANK, ...address } : BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const us = isUS(f.country);

  const save = async (e) => {
    e.preventDefault();
    const req = us
      ? [["name", "Full name"], ["street1", "Street address"], ["city", "City"], ["state", "State"], ["zip", "ZIP code"]]
      : [["name", "Full name"], ["street1", "Street address"], ["city", "City"], ["zip", "Postal code"]];
    for (const [k, label] of req) {
      if (!String(f[k]).trim()) return toast.error(`${label} is required`);
    }
    setSaving(true);
    try {
      const body = { name: f.name.trim(), phone: f.phone, street1: f.street1.trim(), street2: f.street2, city: f.city.trim(), state: f.state.trim(), zip: f.zip.trim(), country: f.country || "US", is_default: !!f.is_default };
      if (editing) await api.patch(`/addresses/${address.id}`, body);
      else await api.post("/addresses", body);
      toast.success(editing ? "Address updated" : "Address saved");
      onSaved();
    } catch (err) { toast.error(err.message || "Could not save"); } finally { setSaving(false); }
  };

  const field = (k, label, ph) => (
    <div style={{ minWidth: 0 }}><label className="ss-label">{label}</label><div className="ss-field" style={{ height: 44 }}><input placeholder={ph} value={f[k]} onChange={set(k)} data-testid={`address-${k}`} /></div></div>
  );

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <form className="ss-modal" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="address-modal" style={{ maxHeight: "90vh", overflowY: "auto", overflowX: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ fontSize: 20 }}>{editing ? "Edit address" : "Add address"}</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="address-close"><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <label className="ss-label">Country</label>
            <div className="ss-field" style={{ height: 44 }}>
              <Globe size={16} className="ss-muted" />
              <select value={f.country || "US"} onChange={(e) => setF((s) => ({ ...s, country: e.target.value }))} data-testid="address-country"
                style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", outline: "none", fontSize: 15, cursor: "pointer", color: "var(--text)" }}>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
          </div>
          {field("name", "Full name", "Jane Doe")}
          {field("phone", "Phone (optional)", "555-123-4567")}
          {field("street1", "Street address", "123 Main St")}
          {field("street2", "Apt / Suite (optional)", "Apt 4B")}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, alignItems: "end" }}>
            {field("city", "City", us ? "Dallas" : "City")}
            {field("state", us ? "State" : "Region (opt.)", us ? "TX" : "Region")}
            {field("zip", postalLabel(f.country), us ? "75201" : "Postal")}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={f.is_default} onChange={(e) => setF((s) => ({ ...s, is_default: e.target.checked }))} data-testid="address-default" />
            <span style={{ fontWeight: 700 }}>Set as default address</span>
          </label>
        </div>
        <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} disabled={saving} data-testid="save-address">{saving ? "Saving…" : "Save address"}</button>
      </form>
    </div>
  );
}

export default function Addresses() {
  const [addresses, setAddresses] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/addresses").then((d) => setAddresses(d.addresses || [])).catch(() => setAddresses([]));
  useEffect(() => { load(); }, []);
  if (addresses === null) return <Loading />;

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try { await api.del(`/addresses/${id}`); toast.success("Address deleted"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const makeDefault = async (a) => {
    try { await api.patch(`/addresses/${a.id}`, { ...a, is_default: true }); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fade-up">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <PageHead title="Your Addresses" sub="Saved shipping addresses for faster checkout." />
        <button className="ss-btn ss-btn-primary" onClick={() => setCreating(true)} data-testid="add-address-button"><Plus size={16} /> Add address</button>
      </div>

      {addresses.length === 0 ? (
        <Empty icon={MapPin} title="No saved addresses" sub="Add an address to check out faster next time." />
      ) : (
        <div className="ss-grid grid-2" data-testid="addresses-list">
          {addresses.map((a) => (
            <div key={a.id} className="ss-card" style={{ padding: 18, position: "relative" }} data-testid={`address-card-${a.id}`}>
              {a.is_default && (
                <span style={{ position: "absolute", top: 14, right: 14, display: "inline-flex", alignItems: "center", gap: 4, background: "var(--gold-soft)", color: "var(--brand)", fontSize: 11, fontWeight: 800, padding: "3px 9px", borderRadius: 999 }} data-testid={`address-default-badge-${a.id}`}><Star size={11} /> Default</span>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, marginBottom: 6 }}><Home size={16} /> {a.name}</div>
              <div className="ss-muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                {a.street1}{a.street2 ? `, ${a.street2}` : ""}<br />
                {a.city}{a.state ? `, ${a.state}` : ""} {a.zip}<br />
                {countryName(a.country)}{a.phone ? ` · ${a.phone}` : ""}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                {!a.is_default && <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => makeDefault(a)} data-testid={`set-default-${a.id}`}><Star size={13} /> Make default</button>}
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setEditing(a)} data-testid={`edit-address-${a.id}`}><Pencil size={13} /> Edit</button>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)" }} onClick={() => remove(a.id)} data-testid={`delete-address-${a.id}`}><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <AddressModal address={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}
